import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, FileText, Eye, Download, Trash2, Upload, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { documentsAPI } from '../../services/api';

const getDocStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const ClientDocumentsTab = ({ 
  clientId, 
  clientName,
  documents, 
  canDeleteDocument,
  onRefresh,
  toast 
}) => {
  const [documentDialog, setDocumentDialog] = useState(false);
  const [newDocument, setNewDocument] = useState({ name: '', type: 'tax_return', file: null });
  const [uploading, setUploading] = useState(false);

  const handleCreateDocument = async () => {
    if (!newDocument.name) {
      toast({ title: 'Error', description: 'Please enter a document name', variant: 'destructive' });
      return;
    }
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('name', newDocument.name);
      formData.append('type', newDocument.type);
      formData.append('clientId', clientId);
      formData.append('client', clientName);
      
      if (newDocument.file) {
        formData.append('file', newDocument.file);
      }
      
      await documentsAPI.create(formData);
      toast({ title: 'Success', description: 'Document uploaded successfully' });
      setDocumentDialog(false);
      setNewDocument({ name: '', type: 'tax_return', file: null });
      onRefresh();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({ title: 'Error', description: 'Failed to upload document', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocument = (doc) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    } else {
      toast({ title: 'Info', description: 'No file attached to this document' });
    }
  };

  const handleDownloadDocument = (doc) => {
    if (doc.fileUrl) {
      const link = document.createElement('a');
      link.href = doc.fileUrl;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await documentsAPI.delete(docId);
      toast({ title: 'Success', description: 'Document deleted successfully' });
      onRefresh();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: 'Error', description: 'Failed to delete document', variant: 'destructive' });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Documents</CardTitle>
            <Button size="sm" className="gap-2" onClick={() => setDocumentDialog(true)} data-testid="add-document-btn">
              <Plus className="h-4 w-4" />
              Upload Document
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc) => (
                <div key={doc._id} className="p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <FileText className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{doc.name}</h4>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {doc.size || 'N/A'} • {doc.createdAt ? format(new Date(doc.createdAt), 'MMM dd, yyyy') : 'N/A'}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{doc.type}</Badge>
                          <Badge className={`text-xs ${getDocStatusColor(doc.status)}`}>
                            {(doc.status || 'pending').replace('_', ' ')}
                          </Badge>
                          {doc.fileUrl && (
                            <Badge variant="outline" className="text-xs text-green-600">File Attached</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => handleViewDocument(doc)}
                        data-testid={`view-doc-${doc._id}`}
                      >
                        <Eye className="h-3 w-3" />
                        View
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="gap-1"
                        onClick={() => handleDownloadDocument(doc)}
                        disabled={!doc.fileUrl}
                        data-testid={`download-doc-${doc._id}`}
                      >
                        <Download className="h-3 w-3" />
                        Download
                      </Button>
                      {canDeleteDocument && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteDocument(doc._id)}
                          data-testid={`delete-doc-${doc._id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No documents yet</p>
          )}
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={documentDialog} onOpenChange={setDocumentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Document Name *</Label>
              <Input 
                value={newDocument.name} 
                onChange={(e) => setNewDocument(prev => ({ ...prev, name: e.target.value }))} 
                placeholder="e.g., 2024 Tax Return"
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={newDocument.type} onValueChange={(v) => setNewDocument(prev => ({ ...prev, type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tax_return">Tax Return</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="receipt">Receipt</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="id_document">ID Document</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>File (Optional)</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  id="document-file"
                  className="hidden"
                  onChange={(e) => setNewDocument(prev => ({ ...prev, file: e.target.files[0] }))}
                />
                <label htmlFor="document-file" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    {newDocument.file ? newDocument.file.name : 'Click to upload a file'}
                  </p>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDocument} disabled={uploading}>
              {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientDocumentsTab;
